import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, BedDouble, Bath, Home, Lock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { scrollToId } from "@/lib/scroll";

const soldLabel = (t) => {
  if (!t) return "Recently sold";
  const d = new Date(t * 1000);
  return `Sold ${d.toLocaleDateString("en-CA", { month: "short", year: "numeric" })}`;
};

const scrollToContact = () => scrollToId("#contact");

export default function RecentlySold() {
  const [sold, setSold] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/sold")
      .then((res) => setSold(res.data))
      .catch(() => setSold([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && sold.length === 0) return null;

  return (
    <section id="sold" data-testid="recently-sold-section" className="relative pt-10 pb-12 sm:pt-14 sm:pb-16 bg-[#0D0B0E] overflow-hidden">
      <div className="absolute left-1/2 -top-10 w-[30rem] h-[30rem] -translate-x-1/2 rounded-full bg-[#E890A7]/6 blur-[130px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mb-14 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">02 · Track Record</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-2xl">
              Recently <em className="italic text-[#E890A7]">sold</em> in Lagoon City
            </h2>
            <p className="mt-5 max-w-xl text-slate-400 font-light">
              The canals are moving. Here are homes that have recently changed hands in the
              community — proof of a market Joselle knows dock by dock.
            </p>
          </div>
          <button
            data-testid="sold-cta-top"
            onClick={() => { trackEvent("generate_lead", { method: "sold_showcase" }); scrollToContact(); }}
            className="shrink-0 inline-flex items-center gap-2 rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-6 py-3 text-sm hover:bg-[#E890A7]/10 transition-all duration-300"
          >
            What did they sell for? <ArrowRight size={15} />
          </button>
        </motion.div>

        {loading ? (
          <div data-testid="sold-loading" className="text-slate-500">Loading recent sales…</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="sold-grid">
            {sold.map((s, i) => (
              <motion.article
                key={s.sold_id}
                data-testid={`sold-card-${s.sold_id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="group rounded-3xl overflow-hidden border border-[#E890A7]/12 bg-[#151218] hover:border-[#E890A7]/40 transition-colors duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  {s.image ? (
                    <img
                      src={s.image}
                      alt={`${s.address} — recently sold in Lagoon City`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#1E1A23] flex items-center justify-center text-[#E890A7]/40">
                      <Home size={34} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E]/70 to-transparent" />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#E890A7] text-[#0D0B0E] text-[10px] font-bold tracking-widest uppercase px-3 py-1.5">
                    <BadgeCheck size={12} /> Sold
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl leading-snug">{s.address}</h3>
                  <p className="text-[11px] font-mono tracking-widest uppercase text-slate-500 mt-1">
                    {s.city} · {soldLabel(s.sold_on)}
                  </p>
                  <div className="mt-4 flex items-center gap-4 text-sm text-slate-400">
                    {s.beds ? <span className="flex items-center gap-1.5"><BedDouble size={15} className="text-[#E890A7]" /> {s.beds}</span> : null}
                    {s.baths ? <span className="flex items-center gap-1.5"><Bath size={15} className="text-[#E890A7]" /> {s.baths}</span> : null}
                    {s.property_type ? <span className="truncate text-slate-500">· {s.property_type}</span> : null}
                  </div>
                  <button
                    data-testid={`sold-price-btn-${s.sold_id}`}
                    onClick={() => { trackEvent("generate_lead", { method: "sold_price_request", listing: s.address }); scrollToContact(); }}
                    className="mt-5 w-full flex items-center justify-center gap-2 rounded-full border border-[#E890A7]/25 text-[#F2A7BA] text-xs px-4 py-2.5 hover:bg-[#E890A7]/10 transition-colors"
                  >
                    <Lock size={12} /> See sold price
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <p className="mt-8 text-xs text-slate-600 max-w-2xl font-light">
          Sold prices are shared privately in accordance with MLS® rules — reach out to Joselle for
          the full picture on any recent sale.
        </p>
      </div>
    </section>
  );
}
